import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";

const basePath = `${API_URL.idp}/user`;

const tenantHeaders = (tenantId, extra = {}) => ({
  headers: {
    ...(tenantId ? { tenantId: String(tenantId).toLowerCase() } : {}),
    ...extra,
  },
});

const getAllTenantUsers = async ({
  size = 20,
  index = 0,
  enabled,
  tenantId,
  ...rest
} = {}) => {
  const params = {
    size,
    index,
    ...rest,
  };

  if (typeof enabled === "boolean") {
    params.enabled = enabled;
  }

  return API.get(
    basePath,
    "tenant_users_list",
    params,
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantUserById = async (userId, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.get(
    `${basePath}/${userId}`,
    "tenant_user_by_id",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const searchTenantUsers = async (filters = {}) => {
  return getAllTenantUsers({
    size: filters.size ?? 20,
    index: filters.index ?? 0,
    username: filters.username || undefined,
    email: filters.email || undefined,
    mobile: filters.mobile || undefined,
    tenantId: filters.tenantId,
  });
};

const createTenantUser = async (payload = {}, tenantId) => {
  return API.post(
    basePath,
    "tenant_user_create",
    {
      name: String(payload.name || "").trim(),
      username: String(payload.username || "").trim(),
      password: payload.password,
      roles: Array.isArray(payload.roles)
        ? payload.roles.filter(Boolean).map(String)
        : [],
      clientId: String(payload.clientId || "").trim(),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUser = async (userId, payload = {}, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}`,
    "tenant_user_update",
    {
      mfaEnabled: Boolean(payload.mfaEnabled),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const deleteTenantUser = async (userId, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.delete(
    `${basePath}/${userId}`,
    "tenant_user_delete",
    { mobile: "" },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUserStatus = async (userId, status, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/status`,
    "tenant_user_status",
    {
      status: String(status || "").trim(),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const changeTenantUserPassword = async (userId, newPassword, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/password`,
    "tenant_user_change_password",
    { newPassword },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUserRole = async (userId, roles, tenantId) => {
  if (!userId) {
    throw new Error("User id is missing.");
  }

  return API.put(
    `${basePath}/${userId}/roles`,
    "tenant_user_update_role",
    { roles },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

export const tenantUserService = {
  getAllTenantUsers,
  getTenantUserById,
  searchTenantUsers,
  createTenantUser,
  updateTenantUser,
  deleteTenantUser,
  updateTenantUserStatus,
  changeTenantUserPassword,
  updateTenantUserRole,
};
